// UX event tracker core (spec section 2): breadcrumb ring, event queue, flag state, batching.
// Pure: transport, storage and clock are injected so it runs under `node --test`.
import { scrubMessage } from './scrub.mjs';

export const EVENTS = ['screen_view', 'action_done', 'action_abandoned', 'validation_error', 'search_empty', 'slow'];
export const QUEUE_KEY = 'ux_event_queue';
export const MAX_QUEUE = 1000;
export const FLUSH_AT = 50;
export const MAX_BATCH = 200;
export const BREADCRUMBS = 10;
export const SESSION_KEY = 'ux_session';
export const SESSION_IDLE_MS = 30 * 60 * 1000;
export const NO_SCREEN = '(tanpa layar)';

function genId() {
  try {
    if (globalThis.crypto && globalThis.crypto.randomUUID) return globalThis.crypto.randomUUID();
  } catch {
    /* ignore */
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
}

// UX session: a new id after SESSION_IDLE_MS without an event, and on rotate() (login / logout).
// Before 2026-10-24 one id lived all day per tab (srs, larissa, cahaya-motor: one session per till per day).
// { id, last } persists in `storage` (sessionStorage) so a reload inside the idle window keeps the session.
export function createSession({ storage = null, now = () => Date.now(), idleMs = SESSION_IDLE_MS, newId = genId } = {}) {
  let cur = null;
  try {
    const s = storage && JSON.parse(storage.getItem(SESSION_KEY) || 'null');
    if (s && typeof s.id === 'string' && typeof s.last === 'number') cur = s;
  } catch {
    cur = null;
  }
  function save() {
    try {
      if (storage) storage.setItem(SESSION_KEY, JSON.stringify(cur));
    } catch {
      /* blocked */
    }
  }
  function rotate() {
    cur = { id: newId(), last: now() };
    save();
    return cur.id;
  }
  return {
    // the id for an event happening now; rotates first when the session went idle
    touch() {
      const t = now();
      if (!cur || t - cur.last > idleMs) rotate();
      cur.last = t;
      save();
      return cur.id;
    },
    rotate,
    get id() {
      return cur ? cur.id : null;
    },
  };
}

// Never an empty screen (jms, 2026-10-24: a `slow` from an API call before the route had a name).
export function screenName(...candidates) {
  for (const c of candidates) {
    const s = c == null ? '' : String(c).trim();
    if (s && !['undefined', 'null'].includes(s.toLowerCase())) return s.slice(0, 120);
  }
  return NO_SCREEN;
}

// Only these meta keys ever leave the browser; values are scalars cut to 48 chars.
const META_KEYS = ['from', 'action', 'entity', 'form', 'field', 'where', 'term_len', 'what'];

export function cleanMeta(meta) {
  if (!meta || typeof meta !== 'object') return null;
  const out = {};
  for (const k of META_KEYS) {
    const v = meta[k];
    if (v == null) continue;
    if (typeof v === 'number') out[k] = Number.isFinite(v) ? Math.round(v) : null;
    else if (typeof v === 'boolean') out[k] = v;
    else out[k] = scrubMessage(String(v), 48);
  }
  return Object.keys(out).length ? out : null;
}

// sessionId: a string (fixed) or a function called per event (createSession().touch).
export function createTracker({ storage = null, send, now = () => Date.now(), app, appVersion, sessionId, getScreen = () => '' }) {
  const sid = typeof sessionId === 'function' ? sessionId : () => sessionId;
  // enabled: null = not known yet (buffer, don't send), true = send, false = collect nothing.
  let enabled = null;
  let queue = [];
  const crumbs = [];
  let flushing = false;
  let paused = false; // 401: wait for a new token

  try {
    const saved = storage && JSON.parse(storage.getItem(QUEUE_KEY) || '[]');
    if (Array.isArray(saved)) queue = saved.slice(-MAX_QUEUE);
  } catch {
    queue = [];
  }

  function persist() {
    try {
      if (!storage) return;
      if (queue.length) storage.setItem(QUEUE_KEY, JSON.stringify(queue));
      else storage.removeItem(QUEUE_KEY);
    } catch {
      /* full or blocked */
    }
  }

  function crumb(event, detail) {
    crumbs.push(`${event}:${String(detail || '').slice(0, 60)}`);
    if (crumbs.length > BREADCRUMBS) crumbs.splice(0, crumbs.length - BREADCRUMBS);
  }

  function track(event, { screen, duration_ms = null, meta = null } = {}) {
    try {
      if (!EVENTS.includes(event)) return null;
      let cur = '';
      try {
        cur = getScreen();
      } catch {
        cur = '';
      }
      const scr = screenName(screen, cur);
      const m = cleanMeta(meta);
      crumb(event, event === 'action_done' && m?.action ? m.action : scr);
      if (enabled === false) return null;
      const ev = {
        app,
        app_version: appVersion,
        session_id: sid(),
        screen: scr,
        event,
        duration_ms: duration_ms == null ? null : Math.max(0, Math.round(duration_ms)),
        meta: m,
        occurred_at: new Date(now()).toISOString(),
      };
      queue.push(ev);
      if (queue.length > MAX_QUEUE) queue.splice(0, queue.length - MAX_QUEUE);
      persist();
      if (enabled === true && queue.length >= FLUSH_AT) flush();
      return ev;
    } catch {
      return null;
    }
  }

  function setEnabled(on) {
    if (on === enabled) return;
    enabled = on;
    if (on === false) {
      queue = [];
      persist();
    }
  }

  // send(events) -> Promise<{status, ux}>: ux = X-Ux-Analytics header ('on'|'off'|null).
  async function flush(max = MAX_BATCH) {
    if (enabled !== true || flushing || paused || !queue.length) return;
    flushing = true;
    try {
      const batch = queue.slice(0, max);
      let res;
      try {
        res = await send(batch);
      } catch {
        return; // network: keep the queue
      }
      const status = res?.status ?? 0;
      if (res?.ux === 'off' || status === 404) {
        setEnabled(false);
        return;
      }
      if (status === 401) {
        paused = true;
        return;
      }
      if (status >= 200 && status < 300) {
        queue.splice(0, batch.length);
        persist();
      }
      // 5xx / 0: keep and retry on the next tick
    } finally {
      flushing = false;
    }
  }

  return {
    track,
    flush,
    setEnabled,
    resume() {
      paused = false;
    },
    get enabled() {
      return enabled;
    },
    get queue() {
      return queue;
    },
    breadcrumbs() {
      return crumbs.slice();
    },
  };
}
